/** Matches date-only strings in ISO 8601 format: YYYY-MM-DD */
export const dateOnlyPattern = /^\d{4}-\d{2}-\d{2}$/;

export type DateOnlyString = `${number}-${number}-${number}`;

/** Matches date-only strings in ISO 8601 format: YYYY-MM-DD */
export const isDateOnlyString = (value: unknown): value is DateOnlyString => {
  return typeof value === 'string' && dateOnlyPattern.test(value);
};

/**
 * Try to convert a value to a date-only string in ISO 8601 format (YYYY-MM-DD).
 * If cannot convert, return the original value.
 * @param value
 * @returns String in ISO 8601 format (YYYY-MM-DD) or the original value if cannot convert
 */
export const toDateOnlyString = <T>(value: T): DateOnlyString | T => {
  if (!value) {
    return value;
  }

  if (value instanceof Date) {
    return value.toISOString().split('T')[0] as DateOnlyString;
  }

  if (isDateOnlyString(value)) {
    return value;
  }

  if (typeof value === 'string' || typeof value === 'number') {
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) {
      return toDateOnlyString(parsed) as DateOnlyString;
    }
  }

  return value;
};

/**
 * Converts a date-only string in ISO 8601 format (YYYY-MM-DD) to a Date object.
 * @param value Date-only string in ISO 8601 format (YYYY-MM-DD)
 * @returns Date object representing the given date-only string
 */
export const toDateOnly = (value: DateOnlyString): Date => {
  const [y, m, d] = value.split('-').map(Number);
  return new Date(y, m - 1, d);
};
